/**
 * Decoration for the sign-in page, drawn from the school's crest: its ring of
 * gold and maroon stars, and the exercise book behind "rewriting the story".
 * Everything here is aria-hidden and ignores the pointer.
 */

const STAR = 'M12 1.8l2.95 6.7 7.3.66-5.52 4.83 1.63 7.15L12 17.4l-6.36 3.74 1.63-7.15L1.75 9.16l7.3-.66z'

export function Star({ className = '', style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className} style={style}>
      <path d={STAR} fill="currentColor" />
    </svg>
  )
}

// Placed by hand to sit around the edges of the panel, clear of the text:
// [left %, top %, size px, opacity, maroon?]
const SCATTER: [number, number, number, number, boolean?][] = [
  [86, 7, 16, 0.55],
  [93, 17, 9, 0.35, true],
  [74, 13, 7, 0.3],
  [95, 41, 12, 0.4],
  [81, 30, 6, 0.25, true],
  [89, 66, 10, 0.3],
  [96, 78, 14, 0.45, true],
  [70, 92, 8, 0.3],
  [52, 95, 6, 0.25, true],
  [83, 88, 7, 0.35],
  [62, 5, 6, 0.22],
]

export function StarField() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      {SCATTER.map(([left, top, size, opacity, maroon], index) => (
        <Star
          key={index}
          className={`absolute ${maroon ? 'text-[#df7268]' : 'text-[#ebcd7c]'}`}
          style={{ left: `${left}%`, top: `${top}%`, width: size, height: size, opacity }}
        />
      ))}
    </div>
  )
}

/** Faint ruled lines with a red margin, like an open exercise book, fading out at the edges. */
export function RuledPage() {
  const fade = 'radial-gradient(ellipse 70% 65% at 35% 50%, black 35%, transparent 75%)'
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0"
      style={{
        backgroundImage:
          'linear-gradient(to right, transparent 0, transparent 5.5rem, rgba(223,114,104,0.22) 5.5rem, rgba(223,114,104,0.22) calc(5.5rem + 1px), transparent calc(5.5rem + 1px)),' +
          'repeating-linear-gradient(to bottom, transparent 0, transparent 37px, rgba(235,205,124,0.075) 37px, rgba(235,205,124,0.075) 38px)',
        maskImage: fade,
        WebkitMaskImage: fade,
      }}
    />
  )
}

/** A hand-drawn stroke under a word, as if underlined in gold ink. */
export function Swoosh({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 200 16" preserveAspectRatio="none" aria-hidden="true" className={className}>
      <path d="M3 11.5C48 4.5 118 2.5 197 7.5" fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
      <path d="M26 14c42-3.5 92-4.8 148-2.6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" opacity="0.55" />
    </svg>
  )
}

// A few stars on the cream side, in the corners, so the two halves share the crest's ring.
const CORNERS: [number, number, number, number, boolean?][] = [
  [8, 9, 14, 0.5, true],
  [15, 17, 7, 0.45],
  [5, 26, 9, 0.35],
  [91, 86, 13, 0.45],
  [84, 93, 7, 0.4, true],
  [95, 74, 8, 0.35],
  [90, 8, 6, 0.3, true],
  [7, 90, 6, 0.3],
]

export function CornerStars() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      {CORNERS.map(([left, top, size, opacity, maroon], index) => (
        <Star
          key={index}
          className={`absolute ${maroon ? 'text-[#8c1d18]' : 'text-[#c9a961]'}`}
          style={{ left: `${left}%`, top: `${top}%`, width: size, height: size, opacity }}
        />
      ))}
    </div>
  )
}
